import { useAuthStore } from "@/lib/store/authStore";
import type { FrappeListResponse } from "@/lib/frappe-client";
import { getERPClient } from "./client";

/**
 * Utility function to get ERP client (mode-aware)
 * In control_plane mode: uses JWT-based tenantServer
 * In direct mode: uses session-based FrappeClient
 */
function getClient() {
  return getERPClient();
}

/**
 * @deprecated Use getClient() instead
 * Legacy function for backwards compatibility in direct mode only
 */
function _getFrappeClientLegacy() {
  const client = useAuthStore.getState().frappeClient;
  if (!client) {
    throw new Error("Not authenticated. Please log in.");
  }
  return client;
}

// Export for backwards compat if needed elsewhere
export { _getFrappeClientLegacy as getFrappeClientFromStore };

/**
 * List documents from Frappe (legacy - returns array only)
 */
export async function frappeList<T>(
  doctype: string,
  filters?: Record<string, any>,
  fields?: string[]
): Promise<T[]> {
  const client = getClient();
  return client.list<T>({
    doctype,
    filters,
    fields,
  });
}

/**
 * List documents from Frappe with pagination metadata
 * Total count handling is now done in ResponseHandler
 */
export async function frappeListWithPagination<T>(
  doctype: string,
  params: {
    filters?: any[] | Record<string, any>; // Support both array and object formats
    or_filters?: Array<[string, string, any]>; // Canonical format: [field, operator, value] (no doctype prefix)
    fields?: string[];
    limit_start?: number;
    limit_page_length?: number;
    order_by?: string;
  }
): Promise<FrappeListResponse<T>> {
  const client = getClient();
  // ResponseHandler already handles getCount fallback when needed
  return client.listWithPagination<T>({
    doctype,
    ...params,
  });
}

/**
 * Get a single document from Frappe
 */
export async function frappeGet<T>(doctype: string, name: string): Promise<T> {
  const client = getClient();
  return client.get<T>({ doctype, name });
}

/**
 * Call a Frappe method
 */
export async function frappeMethod<T>(
  method: string,
  args?: Record<string, any>
): Promise<T> {
  const client = getClient();
  return client.call<T>(method, args);
}

/**
 * List documents using Frappe's ListView API (POST /api/method/frappe.desk.reportview.get_list)
 * This API fully respects DocType get_list overrides (e.g., ItemList override with ToDo JOIN)
 * 
 * IMPORTANT: 
 * - filters MUST always be in array format for override to work
 * - _assigned_to is sent separately as assigned_to in POST body to avoid Frappe field validation errors
 *   (Frappe validates filter fields and rejects _assigned_to before override runs)
 * - POST form_dict is NOT sanitized, so assigned_to reaches the override via frappe.form_dict
 */
export async function frappeListWithPaginationListView<T>(
  doctype: string,
  params: {
    filters?: any[]; // MUST always be array format for override to work
    or_filters?: Array<[string, string, any]>;
    fields?: string[];
    limit_start?: number;
    limit_page_length?: number;
    order_by?: string;
    assigned_to?: any; // Separate parameter for _assigned_to - sent in POST body, read from frappe.form_dict
  }
): Promise<FrappeListResponse<T>> {
  const client = getClient();

  // Build args - include assigned_to as separate parameter in POST body
  const args: Record<string, any> = {
    doctype,
    fields: params.fields || ["name"],
    filters: params.filters || [],
    or_filters: params.or_filters || [],
    limit_start: params.limit_start || 0,
    limit_page_length: params.limit_page_length || 20,
    order_by: params.order_by || "modified desc",
  };

  // Add assigned_to as separate parameter (bypasses field validation via POST body)
  if (params.assigned_to !== undefined) {
    args.assigned_to = params.assigned_to;
  }

  // Call frappe.desk.reportview.get_list - this supports overrides
  // POST body is passed to frappe.form_dict, which override can read
  const result = await client.call<any>("frappe.desk.reportview.get_list", args);

  // The List API returns data directly (array), not wrapped in { data, total_count }
  const data = Array.isArray(result) ? result : result?.data || [];

  // Get total count if needed (for pagination)
  let totalCount = data.length; // Fallback to data length
  try {
    // Convert array filters to object format for getCount (if needed)
    // getCount accepts both formats, but array is preferred
    totalCount = await client.getCount(doctype, params.filters as any);
  } catch (e) {
    // If count fails, use data length as fallback
    // This is acceptable for pagination - user will see "showing X of at least Y"
  }

  return {
    data: data as T[],
    // @ts-expect-error - Property may be intentionally dynamic
    total_count: totalCount,
    limit_page_length: params.limit_page_length || 20,
  };
}

